import { useEffect, useState } from 'react';

import { css } from '@emotion/css';
import { Row, Space, Col } from 'antd';
import * as d3 from 'd3';
import { useDispatch, useSelector } from 'react-redux';
import { doGetQuoteChart } from 'src/actions';
import { GetQuoteChartQueryParamIntervalDto, QuoteDetailsDto } from 'src/dtos';
import { MSpinner, MTypography, PercentageField, Chart } from 'src/lib';
import { mapQueryParamIntervalDtoToChartOptions } from 'src/mappers';
import { GetQuoteChartResponse } from 'src/models';
import { BaseStyle, ColorConstant } from 'src/styles';
import { Maybe } from 'src/utils';

interface SecurityDetailsChartFilterIntervalFieldProps {
  value: string;
  isActive?: boolean;
}

export const SecurityDetailsChartFilterIntervalField = ({
  value,
  isActive = false,
}: SecurityDetailsChartFilterIntervalFieldProps) => {
  return (
    <MTypography variant='subtitle1' color={ColorConstant.BRAND6} weight={isActive ? 'bold' : 'normal'}>
      {value}
    </MTypography>
  );
};

const securityDetailsChartFilterSpaceStyle = css`
  cursor: pointer;
`;

interface SecurityDetailsChartFilterProps {
  value: GetQuoteChartQueryParamIntervalDto;
  onChange: (value: GetQuoteChartQueryParamIntervalDto) => void;
}

export const SecurityDetailsChartFilter = ({ value, onChange }: SecurityDetailsChartFilterProps) => {
  return (
    <Row className={BaseStyle.level}>
      <Space size={16} className={securityDetailsChartFilterSpaceStyle}>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.OneDay)}>
          <SecurityDetailsChartFilterIntervalField
            value='1D'
            isActive={value === GetQuoteChartQueryParamIntervalDto.OneDay}
          />
        </Col>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.OneWeek)}>
          <SecurityDetailsChartFilterIntervalField
            value='1W'
            isActive={value === GetQuoteChartQueryParamIntervalDto.OneWeek}
          />
        </Col>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.OneMonth)}>
          <SecurityDetailsChartFilterIntervalField
            value='1M'
            isActive={value === GetQuoteChartQueryParamIntervalDto.OneMonth}
          />
        </Col>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.SixMonths)}>
          <SecurityDetailsChartFilterIntervalField
            value='6M'
            isActive={value === GetQuoteChartQueryParamIntervalDto.SixMonths}
          />
        </Col>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.OneYear)}>
          <SecurityDetailsChartFilterIntervalField
            value='1Y'
            isActive={value === GetQuoteChartQueryParamIntervalDto.OneYear}
          />
        </Col>
        <Col onClick={() => onChange(GetQuoteChartQueryParamIntervalDto.All)}>
          <SecurityDetailsChartFilterIntervalField
            value='ALL'
            isActive={value === GetQuoteChartQueryParamIntervalDto.All}
          />
        </Col>
      </Space>
    </Row>
  );
};

interface SecurityDetailsChartProps {
  symbol?: string;
}

export const SecurityDetailsChart = ({ symbol }: SecurityDetailsChartProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => Boolean(state.quotes.chart.isLoading));
  const chartData: Maybe<GetQuoteChartResponse> = useSelector((state: any) => state.quotes.chart.data);
  const quoteDetails: Maybe<QuoteDetailsDto> = useSelector((state: any) => state.quotes.details.data);
  const [filter, setFilter] = useState<GetQuoteChartQueryParamIntervalDto>(GetQuoteChartQueryParamIntervalDto.OneDay);
  const [series, setSeries] = useState<[number, number][]>([]);
  const [options, setOptions] = useState<{
    xAxis?: { tickInterval?: d3.CountableTimeInterval; tickFormat?: string; tickStep?: number };
  }>({
    xAxis: {},
  });

  useEffect(() => {
    const newOptions = mapQueryParamIntervalDtoToChartOptions(filter);
    setOptions(newOptions);
  }, [filter]);

  useEffect(() => {
    if (
      filter === GetQuoteChartQueryParamIntervalDto.OneDay ||
      filter === GetQuoteChartQueryParamIntervalDto.OneWeek ||
      filter === GetQuoteChartQueryParamIntervalDto.OneMonth
    ) {
      const daySeries: [number, number][] =
        chartData?.intraday?.[0]?.interval?.map(item => [new Date(item.startdatetime).valueOf(), item.close]) ?? [];
      setSeries(daySeries);

      return;
    }

    const historySeries: [number, number][] =
      chartData?.history?.[0]?.eoddata?.map(item => [new Date(item.date).valueOf(), item.close]) || [];
    setSeries(historySeries);
  }, [chartData, filter]);

  useEffect(() => {
    if (chartData?.symbol === symbol) return;

    if (symbol) {
      dispatch(doGetQuoteChart({ params: { symbol }, queryParams: { interval: filter } }));
    }
  }, [symbol, chartData?.symbol, filter, dispatch]);

  if (isLoading) {
    return <MSpinner height='300px' />;
  }

  return (
    <Row>
      {quoteDetails?.pricedata?.change && (
        <Col span={24}>
          <MTypography variant='h6' color={ColorConstant.BRAND6} align='center'>
            <PercentageField
              value={quoteDetails?.pricedata.change}
              color={quoteDetails.pricedata.change > 0 ? ColorConstant.GREEN6 : ColorConstant.RED6}
            />
          </MTypography>
        </Col>
      )}
      <Col span={24}>
        <SecurityDetailsChartFilter
          value={filter}
          onChange={value => {
            setFilter(value);

            if (symbol) {
              dispatch(doGetQuoteChart({ params: { symbol }, queryParams: { interval: value } }));
            }
          }}
        />
      </Col>
      <Col span={24} className={BaseStyle.level}>
        <Chart data={series} width={490} height={275} options={options} />
      </Col>
    </Row>
  );
};
